import { useCallback } from 'react'
import type { CardTextWrite } from '../types/cardWrite'
import { normalizeDashboardConfig } from '../utils/dashboardConfig'
import axios from 'axios'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { extractErrorMessage } from '../utils/apitools'
import { promotionWarning, type DashboardForeignRef } from '../utils/dashboardPromotion'
import { dashboardNameConflictMessage } from '../utils/dashboardOwnership'
import useAccount from './useAccount'
import useSnackbar from './useSnackbar'
import { API_BASE_URL } from '../constants/system'
import { Dashboard, DashboardConfig, DashboardShareResponse } from '../types/gotypes'

interface ListDashboardsResponse {
  customer: string
  dashboards: Dashboard[]
  count: number
}

export const useDashboards = ({
  customer,
  dashboardId,
  job,
  allJobs,
}: {
  customer: string
  dashboardId?: string
  /** Job-scope the list query (#962) — omit for the single-dashboard-only use case. */
  job?: string
  /** #1162 "All DataViz": list every job the caller holds. Overrides job. */
  allJobs?: boolean
}) => {
  const account = useAccount()
  const snackbar = useSnackbar()
  const queryClient = useQueryClient()

  const listQueryKey = ['dashboards', { customer, job, allJobs }]
  const singleQueryKey = ['dashboard', { customer, dashboardId }]

  // List dashboards query — job-scoped (#962): only fires once a job is
  // selected, so the list is never accidentally "every dashboard for every
  // job at once". #1162's "All DataViz" is that deliberately: allJobs sends
  // no job param, and the server returns every dashboard in every job the
  // caller holds (the whole customer for a Manager or Realm admin).
  const listQuery = useQuery({
    queryKey: listQueryKey,
    enabled: Boolean(customer && (job || allJobs) && account.user),
    queryFn: async () => {
      if (!customer || !(job || allJobs) || !account.user) return []
      try {
        const res = await axios.get<ListDashboardsResponse>(
          `${API_BASE_URL}/dashboards/${customer}`,
          { params: allJobs ? {} : { job } }
        )
        return res.data.dashboards
      } catch (error) {
        throw new Error(extractErrorMessage(error))
      }
    },
  })

  // Get single dashboard query.
  //
  // #1297: always fresh on mount. The body carries `resolved_themes` — the
  // live palette of every brand theme the config references, resolved
  // server-side on every read — and the Editor and the View page share this
  // one cache entry. Under App.tsx's five-minute staleTime, opening View
  // after changing a theme in Edit was served the Editor's cached body and
  // never went to the network, so the old colours stayed until a hard refresh
  // threw the whole QueryClient away. A dashboard is one small GET; there is
  // nothing to gain by serving a stale one.
  const singleQuery = useQuery({
    queryKey: singleQueryKey,
    staleTime: 0,
    refetchOnMount: 'always',
    enabled: Boolean(customer && dashboardId && dashboardId !== 'new' && account.user),
    queryFn: async () => {
      if (!customer || !dashboardId || dashboardId === 'new' || !account.user) return null
      try {
        const res = await axios.get<Dashboard>(
          `${API_BASE_URL}/dashboards/${customer}/${dashboardId}`
        )
        // #1245: `layout.rows` arrives as null for API-created dashboards;
        // normalise once here so no walker downstream has to guard it.
        return res.data?.config
          ? { ...res.data, config: normalizeDashboardConfig(res.data.config) }
          : res.data
      } catch (error) {
        throw new Error(extractErrorMessage(error))
      }
    },
  })

  // Create dashboard mutation
  const createMutation = useMutation({
    mutationFn: async ({ config, job }: { config: DashboardConfig; job: string }) => {
      if (!customer) {
        throw new Error('Customer is required')
      }
      // #961: a dashboard is scoped to one job, set here and immutable after.
      // The server rejects a job-less create; failing here gives a better
      // message than a 400 from the API.
      if (!job) {
        throw new Error('Job is required')
      }

      const dashboard = {
        customer,
        user: account.user?.session_info?.email || '',
        job,
        config,
      }

      const res = await axios.post<Dashboard>(
        `${API_BASE_URL}/dashboards/${customer}`,
        dashboard
      )
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: listQueryKey })
    },
    onError: (error) => {
      // #1274: a name clash names the dashboard and its owner.
      snackbar.error(dashboardNameConflictMessage(error) ?? extractErrorMessage(error))
    },
  })

  // Update dashboard mutation with debouncing
  const updateMutation = useMutation({
    mutationFn: async ({ id, config, expectedUpdatedAt }: { id: string; config: DashboardConfig; expectedUpdatedAt?: number }) => {
      if (!customer) {
        throw new Error('Customer is required')
      }

      const dashboard = {
        id,
        customer,
        user: account.user?.session_info?.email || '',
        config,
        // Optimistic-concurrency precondition: when the caller knows which
        // version it read, the server rejects the save with 409 if the stored
        // row has moved on, instead of overwriting the other person's edit.
        ...(expectedUpdatedAt ? { updated_at: expectedUpdatedAt } : {}),
      }

      const res = await axios.put<Dashboard>(
        `${API_BASE_URL}/dashboards/${customer}/${id}`,
        dashboard
      )
      return res.data
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: listQueryKey })
      if (dashboardId) {
        // Immediately update the cache so navigating to view/preview sees fresh data
        queryClient.setQueryData(singleQueryKey, data)
      }
    },
    onError: (error) => {
      // 403 means "you don't own this — fork instead" (#962). It is surfaced,
      // never swallowed: an auto-save that silently fails leaves the user
      // typing into a dashboard that is not being saved.
      if (axios.isAxiosError(error) && error.response?.status === 403) {
        snackbar.error("You don't own this dashboard, so it can't be saved in place. Use \u201cSave to My dashboards\u201d to keep your changes.")
        return
      }
      // #1274: a rename onto another dashboard's name is a 409 too, but it is
      // NOT the concurrency case below — reloading would throw the rename
      // away. The server marks it conflict.kind = "name".
      const nameClash = dashboardNameConflictMessage(error)
      if (nameClash) {
        snackbar.error(nameClash)
        return
      }
      if (axios.isAxiosError(error) && error.response?.status === 409) {
        // Someone else saved while this copy was open. Pull the latest version
        // in so the user is looking at the real state before retrying.
        queryClient.invalidateQueries({ queryKey: listQueryKey })
        if (dashboardId) {
          queryClient.invalidateQueries({ queryKey: singleQueryKey })
        }
        snackbar.error('Someone else changed this dashboard. Reloading the latest version — please make your change again.')
        return
      }
      snackbar.error(extractErrorMessage(error))
    },
  })

  // Fork ("Save to My dashboards") — clone-on-save for non-owners (#962)
  const forkMutation = useMutation({
    mutationFn: async ({ id, config }: { id: string; config?: DashboardConfig }) => {
      const res = await axios.post<Dashboard>(
        `${API_BASE_URL}/dashboards/${customer}/${id}/fork`,
        config ? { config } : {}
      )
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: listQueryKey })
    },
    onError: (error) => {
      snackbar.error(extractErrorMessage(error))
    },
  })

  // Promote/demote to company-level — Manager/Realm only (#962)
  const promoteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await axios.post<Dashboard & { foreign_refs?: DashboardForeignRef[] }>(`${API_BASE_URL}/dashboards/${customer}/${id}/promote`)
      return res.data
    },
    onSuccess: ({ foreign_refs, ...data }) => {
      queryClient.invalidateQueries({ queryKey: listQueryKey })
      queryClient.setQueryData(['dashboard', { customer, dashboardId: data.id }], data)
      // Widgets that load someone else's table or chat stay empty for people
      // who may not see that person's work; promotion still goes ahead.
      const warning = promotionWarning(foreign_refs)
      if (warning) snackbar.warning(warning)
    },
    onError: (error) => {
      snackbar.error(extractErrorMessage(error))
    },
  })

  const demoteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await axios.delete<Dashboard>(`${API_BASE_URL}/dashboards/${customer}/${id}/promote`)
      return res.data
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: listQueryKey })
      queryClient.setQueryData(['dashboard', { customer, dashboardId: data.id }], data)
    },
    onError: (error) => {
      snackbar.error(extractErrorMessage(error))
    },
  })

  // Job set mutation (#1162) — the ONLY way a dashboard's job set changes.
  // Server refuses removing the primary or a job still referenced by a widget,
  // surfaced as a snackbar. Both invalidate the single + list queries so the
  // editor re-reads the new set.
  const addJobMutation = useMutation({
    mutationFn: async ({ id, job }: { id: string; job: string }) => {
      const res = await axios.post<Dashboard>(`${API_BASE_URL}/dashboards/${customer}/${id}/jobs`, { job })
      return res.data
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: listQueryKey })
      queryClient.setQueryData(['dashboard', { customer, dashboardId: data.id }], data)
    },
    onError: (error) => {
      snackbar.error(extractErrorMessage(error))
    },
  })

  const removeJobMutation = useMutation({
    mutationFn: async ({ id, job }: { id: string; job: string }) => {
      const res = await axios.delete<Dashboard>(`${API_BASE_URL}/dashboards/${customer}/${id}/jobs/${encodeURIComponent(job)}`)
      return res.data
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: listQueryKey })
      queryClient.setQueryData(['dashboard', { customer, dashboardId: data.id }], data)
    },
    onError: (error) => {
      snackbar.error(extractErrorMessage(error))
    },
  })

  // Delete dashboard mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await axios.delete(`${API_BASE_URL}/dashboards/${customer}/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: listQueryKey })
      snackbar.success('Dashboard deleted successfully')
    },
    onError: (error) => {
      snackbar.error(extractErrorMessage(error))
    },
  })

  const createDashboard = useCallback(
    async (config: DashboardConfig, job: string) => {
      return await createMutation.mutateAsync({ config, job })
    },
    [createMutation]
  )

  const forkDashboard = useCallback(
    async (id: string, config?: DashboardConfig) => {
      return await forkMutation.mutateAsync({ id, config })
    },
    [forkMutation]
  )

  const promoteDashboard = useCallback(
    async (id: string) => {
      return await promoteMutation.mutateAsync(id)
    },
    [promoteMutation]
  )

  const demoteDashboard = useCallback(
    async (id: string) => {
      return await demoteMutation.mutateAsync(id)
    },
    [demoteMutation]
  )

  // #1256: Gen AI card text edit / sign-off for any logged-in user. The
  // response is the updated dashboard, written straight into the cache so
  // the viewer re-renders the new attribution without a refetch.
  const writeCardMutation = useMutation({
    mutationFn: async ({ id, multiTableId, patch, expectedUpdatedAt }: { id: string; multiTableId: string; patch: CardTextWrite; expectedUpdatedAt?: number }) => {
      const res = await axios.put<Dashboard>(
        `${API_BASE_URL}/dashboards/${customer}/${id}/cards/${multiTableId}/text`,
        { ...patch, updated_at: expectedUpdatedAt ?? 0 }
      )
      return res.data
    },
    onSuccess: (data) => {
      queryClient.setQueryData(singleQueryKey, data)
      queryClient.invalidateQueries({ queryKey: listQueryKey })
    },
    onError: (error) => {
      snackbar.error(extractErrorMessage(error))
    },
  })

  const writeDashboardCard = useCallback(
    async (id: string, multiTableId: string, patch: CardTextWrite, expectedUpdatedAt?: number) => {
      await writeCardMutation.mutateAsync({ id, multiTableId, patch, expectedUpdatedAt })
    },
    [writeCardMutation]
  )

  const addDashboardJob = useCallback(
    async (id: string, job: string) => {
      return await addJobMutation.mutateAsync({ id, job })
    },
    [addJobMutation]
  )

  const removeDashboardJob = useCallback(
    async (id: string, job: string) => {
      return await removeJobMutation.mutateAsync({ id, job })
    },
    [removeJobMutation]
  )

  const updateDashboard = useCallback(
    async (id: string, config: DashboardConfig, expectedUpdatedAt?: number) => {
      await updateMutation.mutateAsync({ id, config, expectedUpdatedAt })
    },
    [updateMutation]
  )

  const deleteDashboard = useCallback(
    (id: string) => {
      deleteMutation.mutate(id)
    },
    [deleteMutation]
  )

  // Share dashboard mutation
  const shareMutation = useMutation({
    mutationFn: async ({ id, accessLevel, password }: { id: string; accessLevel: string; password?: string }) => {
      const res = await axios.put<DashboardShareResponse>(
        `${API_BASE_URL}/dashboards/${customer}/${id}/share`,
        { access_level: accessLevel, password: password || '' }
      )
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: listQueryKey })
      if (dashboardId) {
        queryClient.invalidateQueries({ queryKey: singleQueryKey })
      }
      snackbar.success('Sharing updated')
    },
    onError: (error) => {
      snackbar.error(extractErrorMessage(error))
    },
  })

  // Disable share mutation
  const disableShareMutation = useMutation({
    mutationFn: async (id: string) => {
      await axios.delete(`${API_BASE_URL}/dashboards/${customer}/${id}/share`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: listQueryKey })
      if (dashboardId) {
        queryClient.invalidateQueries({ queryKey: singleQueryKey })
      }
      snackbar.success('Sharing disabled')
    },
    onError: (error) => {
      snackbar.error(extractErrorMessage(error))
    },
  })

  const shareDashboard = useCallback(
    async (id: string, accessLevel: string, password?: string) => {
      return await shareMutation.mutateAsync({ id, accessLevel, password })
    },
    [shareMutation]
  )

  const disableShare = useCallback(
    async (id: string) => {
      await disableShareMutation.mutateAsync(id)
    },
    [disableShareMutation]
  )

  // Publish an agent output to DataViz. The server picks the item type from
  // the artifact (a web app or a video), so one route serves every type.
  type PublishArtifactParams = {
    artifactId: string
    name: string
    description: string
    accessLevel: string
    password?: string
  }
  const publishArtifactMutation = useMutation({
    mutationFn: async (params: PublishArtifactParams) => {
      const res = await axios.post<{
        dashboard_id: string
        share_id: string
        access_level: string
        share_enabled: boolean
        type: string
      }>(
        `${API_BASE_URL}/dashboards/${customer}/publish-artifact`,
        {
          artifact_id: params.artifactId,
          name: params.name,
          description: params.description,
          access_level: params.accessLevel,
          password: params.password || '',
        }
      )
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: listQueryKey })
    },
    onError: (error) => {
      snackbar.error(extractErrorMessage(error))
    },
  })

  const publishArtifact = useCallback(
    async (params: PublishArtifactParams) => {
      return await publishArtifactMutation.mutateAsync(params)
    },
    [publishArtifactMutation]
  )

  return {
    // List query
    dashboards: listQuery.data || [],
    listIsLoading: listQuery.isLoading,
    listIsError: listQuery.isError,
    listError: listQuery.error,

    // Single dashboard query
    dashboard: singleQuery.data,
    dashboardIsLoading: singleQuery.isLoading,
    dashboardIsPending: singleQuery.isPending,
    dashboardIsError: singleQuery.isError,
    dashboardError: singleQuery.error,

    // Mutations
    createDashboard,
    createIsLoading: createMutation.isPending,

    updateDashboard,
    updateIsLoading: updateMutation.isPending,

    deleteDashboard,
    deleteIsLoading: deleteMutation.isPending,

    forkDashboard,
    forkIsLoading: forkMutation.isPending,

    promoteDashboard,
    promoteIsLoading: promoteMutation.isPending,

    demoteDashboard,
    demoteIsLoading: demoteMutation.isPending,

    addDashboardJob,
    writeDashboardCard,
    removeDashboardJob,
    jobMutationIsLoading: addJobMutation.isPending || removeJobMutation.isPending,

    shareDashboard,
    shareIsLoading: shareMutation.isPending,

    disableShare,
    disableShareIsLoading: disableShareMutation.isPending,

    publishArtifact,
    publishArtifactIsLoading: publishArtifactMutation.isPending,
  }
}

export default useDashboards
